// Packages
import {
  KiteButton,
  KiteCard,
  KiteCell,
  KiteChip,
  KiteChipGroup,
  KiteColumn,
  KiteDataTable,
  KiteDialog,
  KiteGrid,
  KiteGridCell,
  KiteLabel,
  KitePagination,
  KiteProgressIndicator,
  KiteRow,
  KiteTab,
  KiteTableHeader,
  KiteTabs,
  KiteTag,
  KiteTextInput,
  KiteTextInputIcon,
  KiteToast,
} from '@kite/react-kite';
import jsPDF from 'jspdf';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

// Components
import { PageLayout } from 'components';
import FilterDrawer, {
  IFilterSelection,
} from 'components/Filter/FilterDrawer/FilterDrawer';
import Analytics from 'pages/Analytics/Analytics';

// Store
import { useAppStore } from 'store/AppStore';

// Utils
import { getDeviceIcon } from 'utils/deviceIcons';
import { apiUrl, requestHandler } from 'utils';

// Hooks
import useFetchAllOrderTypes from 'hooks/apiCalls/useFetchAllOrderTypes';
import { useDebounce } from 'hooks';

// Styles
import './Home.scss';

// ─── Constants ───────────────────────────────────────────────────────────────
const MINIMUM_ITEMS_PER_PAGE = 10;
const ITEMS_PER_PAGE_OPTIONS = [10, 15, 25] as const;
const WRIKE_NEW_REQUEST_URL =
  'https://www.wrike.com/frontend/requestforms/index.html?token=eyJhY2NvdW50SWQiOjU5MTY3OTYsInRhc2tGb3JtSWQiOjI1NDg3MDl9CTQ5MTc2OTUxNDM1MTYJYWU2YzYwM2YwNjU2MDE4YTFlNTI3OWVkNDUzZGI5MGNlNzRhYTE3MzZlN2JlNDQ4MDJkY2U5M2ZhNDJkNjgxMw==';

const EXPORT_HEADERS = [
  'Collateral Code',
  'Order Type',
  'Device Type(s)',
  'Line of Business',
  'Current Version',
  'Print Volume',
  'Status',
  'Updated',
] as const;

const SORT_KEY_TO_BACKEND: Partial<Record<keyof IOrderType, string>> = {
  collateralCode: 'collateralCode',
  orderType: 'orderType',
  lob: 'lob',
  collateralVersion: 'collateralVersion',
  printed: 'printVolume',
  active: 'status',
  updated: 'updatedDate',
};

const VALID_SORT_KEYS: (keyof IOrderType)[] = [
  'collateralCode',
  'orderType',
  'lob',
  'collateralVersion',
  'printed',
  'active',
  'updated',
];

const DEFAULT_FILTERS: IFilterSelection = { lob: ['Residential'] };

// ─── Types ───────────────────────────────────────────────────────────────────
type ExportFormat = 'csv' | 'xls' | 'pdf';
type ExportScope = 'all' | 'filtered';

export interface IOrderType {
  lob: string;
  lineOfBusiness: string;
  configKey: string;
  collateralCode: string;
  collateralVersion: string;
  orderType: string;
  servicesRepresented: string;
  modemType: string | null;
  wifiType: string | null;
  xumoVariant: string | null;
  hasBbs: boolean;
  hasInternet: boolean;
  hasWifi: boolean;
  hasVoice: boolean;
  hasXumo: boolean;
  active: boolean;
  lookupServices: string;
  printed: string;
  updated: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const escapeCsvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

const toParam = (values?: string[]) =>
  values && values.length > 0 ? values.join(',') : undefined;

const buildDeviceString = (item: IOrderType): string => {
  const devices = [item.modemType, item.wifiType, item.xumoVariant]
    .filter(Boolean)
    .join(', ');
  return devices + (item.hasBbs ? (devices ? ', BBS' : 'BBS') : '');
};

// ─── Component ───────────────────────────────────────────────────────────────
const Home = () => {
  // State
  const [query, setQuery] = useState('');
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [activeFilters, setActiveFilters] = useState<IFilterSelection>(DEFAULT_FILTERS);
  const [toastText, setToastText] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('error');
  const [centerToastText, setCenterToastText] = useState<string | null>(null);
  const [currentSortKey, setCurrentSortKey] = useState<keyof IOrderType>('collateralCode');
  const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('ascending');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(MINIMUM_ITEMS_PER_PAGE);
  const [currentTab, setCurrentTab] = useState(0);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');
  const [exportScope, setExportScope] = useState<ExportScope>('all');

  // Refs
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);

  // Hooks
  const debouncedSearch = useDebounce(query.trim(), 400);
  const navigate = useNavigate();
  const setOrderType = useAppStore((state) => state.setOrderType);
  const queryClient = useQueryClient();

  // ─── API Call ────────────────────────────────────────────────────────────
  const backendSortBy = SORT_KEY_TO_BACKEND[currentSortKey];
  const backendSortOrder: 'asc' | 'desc' = sortDirection === 'ascending' ? 'asc' : 'desc';

  const { data, isLoading, error } = useFetchAllOrderTypes({
    page: currentPage,
    limit: itemsPerPage,
    lob: toParam(activeFilters.lob),
    orderType: toParam(activeFilters.orderType),
    device: toParam(activeFilters.device),
    status: toParam(activeFilters.status),
    search: debouncedSearch.length >= 3 ? debouncedSearch : undefined,
    sortBy: backendSortBy,
    sortOrder: backendSortOrder,
  });

  // ─── Derived Data ────────────────────────────────────────────────────────
  const orderTypes = useMemo(
    () => (data?.orderList ?? []) as IOrderType[],
    [data?.orderList]
  );

  const totalItems = data?.pagination.total ?? 0;
  const totalPages = Math.max(data?.pagination.totalPages ?? 1, 1);
  const safePage = Math.min(currentPage, totalPages);

  const filterOptions = useMemo(
    () => ({
      lob: data?.filters?.availableLobs ?? [],
      orderType: data?.filters?.availableOrderTypes ?? [],
      device: data?.filters?.availableDevices ?? [],
      status: data?.filters?.availableStatuses ?? [],
    }),
    [data?.filters]
  );

  const activeFilterChips = useMemo(
    () =>
      Object.entries(activeFilters).flatMap(([key, values]) =>
        values.map((value) => ({ key, value }))
      ),
    [activeFilters]
  );

  const analyticsCollateralId = orderTypes[0]?.collateralCode;

  // ─── Handlers (memoized) ─────────────────────────────────────────────────
  const handleViewPdf = useCallback(
    (item: IOrderType) => {
      setOrderType({ ...item });
      navigate('/review-pdf', { state: { orderType: item } });
    },
    [setOrderType, navigate]
  );

  const handleNextPage = useCallback(
    () => setCurrentPage((prev) => Math.min(prev + 1, totalPages)),
    [totalPages]
  );

  const handlePrevPage = useCallback(
    () => setCurrentPage((prev) => Math.max(prev - 1, 1)),
    []
  );

  const handlePageSelect = useCallback((pageNumber: number) => setCurrentPage(pageNumber), []);

  const handleItemsPerPageChange = useCallback((newItemCount: number) => {
    setCurrentPage(1);
    setItemsPerPage(newItemCount);
  }, []);

  const handleClearFilters = useCallback(() => {
    setActiveFilters({});
    setCurrentPage(1);
    setShowFilterDrawer(false);
  }, []);

  const handleApplyFilters = useCallback((filters: IFilterSelection) => {
    setActiveFilters(filters);
    setCurrentPage(1);
    setShowFilterDrawer(false);
  }, []);

  const handleRemoveFilterChip = useCallback((key: string, value: string) => {
    setActiveFilters((prev) => {
      const updated = { ...prev };
      updated[key] = (updated[key] || []).filter((v) => v !== value);
      if (updated[key].length === 0) delete updated[key];
      return updated;
    });
    setCurrentPage(1);
  }, []);

  const handleTabChange = useCallback((value: number) => setCurrentTab(value), []);

  const handleOpenExportDialog = useCallback(() => {
    if (orderTypes.length === 0) {
      setToastType('error');
      setToastText('No data available to export.');
      return;
    }
    setExportFormat('csv');
    setExportScope(activeFilterChips.length > 0 ? 'filtered' : 'all');
    setIsExportDialogOpen(true);
  }, [orderTypes.length, activeFilterChips.length]);

  const handleCloseExportDialog = useCallback(() => setIsExportDialogOpen(false), []);

  const handleSort = useCallback(
    (sortKey: unknown) => {
      if (typeof sortKey === 'string' && VALID_SORT_KEYS.includes(sortKey as keyof IOrderType)) {
        const key = sortKey as keyof IOrderType;
        setSortDirection(
          currentSortKey === key && sortDirection === 'ascending' ? 'descending' : 'ascending'
        );
        setCurrentSortKey(key);
      }
    },
    [currentSortKey, sortDirection]
  );

  // ─── Export Logic ────────────────────────────────────────────────────────
  const getExportRows = useCallback(
    (items: IOrderType[]) =>
      items.map((item) => [
        item.collateralCode ?? '',
        item.lookupServices ?? '',
        buildDeviceString(item),
        item.lineOfBusiness ?? '',
        item.collateralVersion ?? '',
        item.printed ?? '',
        item.active ? 'In Production' : 'Pilot',
        item.updated ?? '',
      ]),
    []
  );

  const downloadBlob = useCallback((blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, []);

  const handleExport = useCallback(async () => {
    setIsExportDialogOpen(false);

    try {
      // Get auth token from query cache
      const tokenData = queryClient.getQueryData<{ token: string }>(['fetchToken']);
      const authHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(tokenData?.token ? { Authorization: `Bearer ${tokenData.token}` } : {}),
      };

      // Use the dedicated export endpoint (no pagination, returns all matching data)
      const queryParams = new URLSearchParams();

      // Only apply filters when scope is 'filtered'
      if (exportScope === 'filtered') {
        const lobParam = toParam(activeFilters.lob);
        const orderTypeParam = toParam(activeFilters.orderType);
        const deviceParam = toParam(activeFilters.device);
        const statusParam = toParam(activeFilters.status);
        if (lobParam) queryParams.append('lob', lobParam);
        if (orderTypeParam) queryParams.append('orderType', orderTypeParam);
        if (deviceParam) queryParams.append('device', deviceParam);
        if (statusParam) queryParams.append('status', statusParam);
        if (debouncedSearch.length >= 3) queryParams.append('search', debouncedSearch);
      }

      if (backendSortBy) queryParams.append('sortBy', backendSortBy);
      queryParams.append('sortOrder', backendSortOrder);

      const url = `${apiUrl({
        endpoint: 'orderTypes/cellular-backup/export',
        apiEnv: import.meta.env.VITE_APP_ENV,
      })}?${queryParams.toString()}`;

      const result = await requestHandler<{ status: number; orderList: IOrderType[] }>({
        requestConfig: {
          method: 'GET',
          url,
          headers: authHeaders,
        },
        responseHandlers: {
          200: (res) => res,
        },
      });

      const exportData = (result?.orderList ?? []) as IOrderType[];

      if (exportData.length === 0) {
        setToastType('error');
        setToastText('No data available to export.');
        return;
      }

      const rows = getExportRows(exportData);
      const dateStr = new Date().toISOString().slice(0, 10);

      if (exportFormat === 'pdf') {
        const doc = new jsPDF({ orientation: 'landscape' });
        let y = 16;
        doc.setFontSize(12);
        doc.text('Order Types Export', 14, y);
        y += 10;
        doc.setFontSize(9);
        doc.text([...EXPORT_HEADERS].join(' | '), 14, y);
        y += 6;
        rows.forEach((row) => {
          if (y > 190) { doc.addPage(); y = 14; }
          const wrapped = doc.splitTextToSize(row.join(' | '), 265);
          doc.text(wrapped, 14, y);
          y += wrapped.length * 5;
        });
        doc.save(`order-types-${dateStr}.pdf`);
        setToastText('Success! Data has been exported as a PDF.');
      } else if (exportFormat === 'xls') {
        const content = [[...EXPORT_HEADERS], ...rows].map((r) => r.join('\t')).join('\n');
        downloadBlob(
          new Blob([content], { type: 'application/vnd.ms-excel;charset=utf-8;' }),
          `order-types-${dateStr}.xls`
        );
        setToastText('Success! Data has been exported as an XLS.');
      } else {
        const csv = [
          [...EXPORT_HEADERS].map(escapeCsvCell).join(','),
          ...rows.map((r) => r.map(escapeCsvCell).join(',')),
        ].join('\n');
        downloadBlob(
          new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
          `order-types-${dateStr}.csv`
        );
        setToastText('Success! Data has been exported as a CSV.');
      }

      setToastType('success');
    } catch (err) {
      setToastType('error');
      setToastText('Failed to export data. Please try again.');
    }
  }, [exportFormat, exportScope, activeFilters, debouncedSearch, backendSortBy, backendSortOrder, getExportRows, downloadBlob, queryClient]);

  // ─── Effects ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (error) {
      setToastType('error');
      setToastText('Failed to load order types. Please try again.');
    }
  }, [error]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, currentSortKey, sortDirection]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    tableContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [currentPage]);

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <PageLayout
      pageTitle="Dashboard"
      className="dashboard"
      addLeadButton={true}
      renderComponent={
        <div className="dashboard-tabs">
          <KiteTabs selectedIndex={currentTab} focusChange={handleTabChange}>
            <KiteTab label="Collateral List" />
            <KiteTab label="Reports & Analytics" />
          </KiteTabs>
        </div>
      }
    >
      {currentTab === 0 && (
        <>
          <div ref={tableContainerRef}>
            {isLoading && (
              <div className="loading-container">
                <KiteProgressIndicator id="kp1" message="Retrieving Data..." title="Loading" />
              </div>
            )}
            {!isLoading && (
              <KiteCard className="data-table-container">
                <KiteGrid>
                  <KiteGridCell col={5}>
                    <span className="search-bar-container">
                      <KiteTextInput
                        value={query}
                        name="search"
                        placeholder="Search by Collateral ID, Device, etc."
                        id="search"
                        className="ent-search-bar"
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          setQuery(e.target.value)
                        }
                      >
                        <KiteLabel>Search</KiteLabel>
                        <KiteTextInputIcon
                          icon="ki-x"
                          onClick={query ? () => setQuery('') : undefined}
                          ariaHidden={!query}
                          slot="right"
                        />
                      </KiteTextInput>
                      <KiteButton
                        className="filter-btn"
                        size="shrinkwrap"
                        variant="secondary"
                        icon="ki-filter"
                        onClick={() => setShowFilterDrawer(true)}
                      >
                        Filters
                      </KiteButton>
                    </span>
                  </KiteGridCell>
                  <KiteGridCell col={7}>
                    <div className="table-actions-container">
                      <KiteButton
                        className="export-btn-no-hover"
                        variant="secondary"
                        size="shrinkwrap"
                        onClick={handleOpenExportDialog}
                      >
                        Export Data
                      </KiteButton>
                      <KiteButton
                        variant="primary"
                        size="shrinkwrap"
                        onClick={() => window.open(WRIKE_NEW_REQUEST_URL, '_blank', 'noopener,noreferrer')}
                      >
                        Request New Collateral
                      </KiteButton>
                    </div>
                  </KiteGridCell>

                  {activeFilterChips.length > 0 && (
                    <KiteGridCell col={12}>
                      <div className="active-filters-row">
                        <span className="active-filters-label">FILTERS</span>
                        <div className="active-filters-chips">
                          <KiteChipGroup>
                            {activeFilterChips.map(({ key, value }) => (
                              <KiteChip
                                key={`${key}-${value}`}
                                label={value}
                                value={value}
                                dismissible
                                selected
                                onClick={() => handleRemoveFilterChip(key, value)}
                              />
                            ))}
                          </KiteChipGroup>
                        </div>
                        <button
                          type="button"
                          className="active-filters-clear-all"
                          onClick={handleClearFilters}
                        >
                          Clear All Filters
                        </button>
                      </div>
                    </KiteGridCell>
                  )}

                  <KiteGridCell col={12}>
                    {orderTypes.length === 0 ? (
                      <div className="empty-state">
                        <strong>No results found.</strong>
                        <p className="empty-state-subtitle">
                          We couldn&apos;t find any order types matching your criteria. Consider
                          using fewer filters for better results.
                        </p>
                      </div>
                    ) : (
                      <KiteDataTable
                        borders="none"
                        zebraStripe="striped"
                        currentSortKey={currentSortKey}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        className="order-types-table"
                        caption="Order Types List"
                      >
                        <KiteTableHeader>
                          <KiteColumn sortKey="collateralCode">Collateral Code</KiteColumn>
                          <KiteColumn sortKey="orderType">Order Type</KiteColumn>
                          <KiteColumn>Device Type</KiteColumn>
                          <KiteColumn sortKey="lob">Line of Business</KiteColumn>
                          <KiteColumn sortKey="collateralVersion">Current Version</KiteColumn>
                          <KiteColumn sortKey="printed">Print Vol.</KiteColumn>
                          <KiteColumn sortKey="active" className="status-column">Status</KiteColumn>
                          <KiteColumn sortKey="updated">Updated</KiteColumn>
                        </KiteTableHeader>
                        <tbody key={`page-${safePage}`}>
                          {orderTypes.map((item, idx) => (
                            <KiteRow key={`${safePage}-${idx}-${item.configKey}`}>
                              <KiteCell>
                                <span
                                  className="collateral-id-link"
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => handleViewPdf(item)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') handleViewPdf(item);
                                  }}
                                >
                                  {item.collateralCode || '-'}
                                </span>
                              </KiteCell>
                              <KiteCell className="order-type-cell">
                                {item.lookupServices || '-'}
                              </KiteCell>
                              <KiteCell>
                                <div className="services-row">
                                  {item.modemType && (
                                    <KiteTag variant="outline" className="kite-tag-equipment" icon={getDeviceIcon(item.modemType)}>
                                      {item.modemType}
                                    </KiteTag>
                                  )}
                                  {item.wifiType && (
                                    <KiteTag variant="outline" className="kite-tag-equipment" icon={getDeviceIcon(item.wifiType)}>
                                      {item.wifiType}
                                    </KiteTag>
                                  )}
                                  {item.hasXumo && item.xumoVariant && (
                                    <KiteTag variant="outline" className="kite-tag-equipment" icon={getDeviceIcon(item.xumoVariant)}>
                                      {item.xumoVariant}
                                    </KiteTag>
                                  )}
                                  {item.hasBbs && (
                                    <KiteTag variant="outline" className="kite-tag-equipment" icon="ki-battery-wifi-7">
                                      BBS
                                    </KiteTag>
                                  )}
                                </div>
                              </KiteCell>
                              <KiteCell>{item.lineOfBusiness || '-'}</KiteCell>
                              <KiteCell>{item.collateralVersion || '-'}</KiteCell>
                              <KiteCell>{item.printed || '-'}</KiteCell>
                              <KiteCell>
                                <KiteTag
                                  variant="outline"
                                  className={`kite-tag-status ${item.active ? 'status-in-production' : 'status-in-pilot'}`}
                                >
                                  {item.active ? 'In Production' : 'Pilot'}
                                </KiteTag>
                              </KiteCell>
                              <KiteCell>{item.updated || '-'}</KiteCell>
                            </KiteRow>
                          ))}
                        </tbody>
                      </KiteDataTable>
                    )}
                  </KiteGridCell>

                  {/* <KiteGridCell col={12}>
                    <div className="auto-activation-note">
                      <span className="auto-activation-icon">⚡</span>
                      <span>Denotes Auto-Activation</span>
                    </div>
                  </KiteGridCell> */}

                  {totalPages > 1 && (
                    <KiteGridCell col={12}>
                      <KitePagination
                        currentPage={safePage}
                        expandUp
                        itemsPerPage={itemsPerPage}
                        itemsPerPageOptions={[...ITEMS_PER_PAGE_OPTIONS]}
                        totalItems={totalItems}
                        onNextPage={handleNextPage}
                        onPrevPage={handlePrevPage}
                        onPageSelect={handlePageSelect}
                        onItemsPerPageChange={handleItemsPerPageChange}
                      />
                    </KiteGridCell>
                  )}
                </KiteGrid>
              </KiteCard>
            )}
          </div>

          <FilterDrawer
            open={showFilterDrawer}
            orderTypes={orderTypes}
            filterOptions={filterOptions}
            onClose={() => setShowFilterDrawer(false)}
            onclearFilter={handleClearFilters}
            onApplyFilters={handleApplyFilters}
            activeFilters={activeFilters}
          />
        </>
      )}

      {currentTab === 1 && <Analytics collateralId={analyticsCollateralId} />}

      <KiteDialog
        id="export-data-dialog"
        open={isExportDialogOpen}
        title="Export Data"
        onClose={handleCloseExportDialog}
        secondaryBtnLabel="Export"
        onSecondaryBtnClick={handleExport}
        tertiaryBtnLabel="Cancel"
        onTertiaryBtnClick={handleCloseExportDialog}
      >
        <div className="export-dialog-content">
          <KiteLabel>Select Export Format</KiteLabel>
          <select
            className="export-format-select"
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
          >
            <option value="csv">Download as CSV</option>
            <option value="xls">Download as XLS</option>
            <option value="pdf">Download as PDF</option>
          </select>

          {activeFilterChips.length > 0 && (
            <div className="export-scope-container">
              <KiteLabel>Do you want to export all order type data?</KiteLabel>
              <label className="export-scope-option">
                <input
                  type="radio"
                  name="exportScope"
                  value="all"
                  checked={exportScope === 'all'}
                  onChange={() => setExportScope('all')}
                />
                Export All Data
              </label>
              <label className="export-scope-option">
                <input
                  type="radio"
                  name="exportScope"
                  value="filtered"
                  checked={exportScope === 'filtered'}
                  onChange={() => setExportScope('filtered')}
                />
                Export Only Filtered Data
              </label>
            </div>
          )}
        </div>
      </KiteDialog>

      <div className="toast-right-aligned">
        <KiteToast
          visible={!!toastText}
          duration="long"
          topOffset={0}
          icon={toastType === 'success' ? 'ki-checkmark-circle-f' : 'ki-caution-circle-f'}
          position="top"
          alignment="right"
          onExpire={() => setToastText(null)}
        >
          {toastText || ''}
        </KiteToast>
      </div>

      <div className="toast-center-alert">
        <KiteToast
          visible={!!centerToastText}
          duration="custom"
          milliseconds={5000}
          topOffset={0}
          icon="ki-caution-circle-f"
          position="top"
          alignment="center"
          onExpire={() => setCenterToastText(null)}
        >
          {centerToastText || ''}
        </KiteToast>
      </div>
    </PageLayout>
  );
};

export default Home;
